import React, { useState, useContext } from "react";
import styled from "styled-components";
import { db } from "../firebase/firebaseConfig";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { toast } from "react-toastify";
import { UserContext } from "../context/UserContext";
import AuthModal from "../components/modal/AuthModal";
import { Upload, X } from "lucide-react";

const PostFreelanceProject = () => {
  const [form, setForm] = useState({
    title: "",
    description: "",
    budget: "",
    duration: "",
    skills: [],
  });
  const [skillInput, setSkillInput] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const { user } = useContext(UserContext);

  const handleAddSkill = (e) => {
    e.preventDefault();
    if (skillInput && !form.skills.includes(skillInput)) {
      setForm((prev) => ({
        ...prev,
        skills: [...prev.skills, skillInput],
      }));
      setSkillInput("");
    }
  };

  const handleRemoveSkill = (skill) => {
    setForm((prev) => ({
      ...prev,
      skills: prev.skills.filter((s) => s !== skill),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return setShowAuthModal(true);
    if (!form.title || !form.description || !form.budget) {
      return toast.error("Please fill all required fields!");
    }

    setLoading(true);
    try {
      await addDoc(collection(db, "freelanceProjects"), {
        ...form,
        fileName: file?.name || null,
        userId: user.id,
        postedBy: user.name,
        createdAt: serverTimestamp(),
        status: "OPEN",
        dev: user,
      });
      toast.success("✅ Project posted successfully!");
      setForm({
        title: "",
        description: "",
        budget: "",
        duration: "",
        skills: [],
      });
      setFile(null);
    } catch (err) {
      console.error("Error posting project:", err);
      toast.error("❌ Failed to post project");
    }
    setLoading(false);
  };

  return (
    <Wrapper>
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      )}

      <Container>
        <h1>Post Freelance Project</h1>
        <p>
          Share your freelance opportunity and connect with top developers to
          get your project done.
        </p>

        <Form onSubmit={handleSubmit}>
          <Input
            placeholder="Project Title *"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />

          <TextArea
            placeholder="Project Description *"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          <FlexRow>
            <Input
              placeholder="Budget (e.g. $500 - $1000) *"
              value={form.budget}
              onChange={(e) => setForm({ ...form, budget: e.target.value })}
            />
            <Select
              value={form.duration}
              onChange={(e) => setForm({ ...form, duration: e.target.value })}
            >
              <option value="">Select Duration</option>
              <option value="1 week">1 Week</option>
              <option value="2 weeks">2 Weeks</option>
              <option value="1 month">1 Month</option>
              <option value="2+ months">2+ Months</option>
            </Select>
          </FlexRow>

          {/* 🔹 Skills Input */}
          <SkillBox>
            <div>
              <SkillInput
                type="text"
                placeholder="Add required skill..."
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
              />
              <AddSkillButton onClick={handleAddSkill}>Add</AddSkillButton>
            </div>
            <SkillTags>
              {form.skills.map((s, i) => (
                <SkillTag key={i}>
                  {s} <X size={14} onClick={() => handleRemoveSkill(s)} />
                </SkillTag>
              ))}
            </SkillTags>
          </SkillBox>

          {/* 🔹 File Upload */}
          <FileUploadLabel>
            <Upload size={18} />
            {file ? file.name : "Attach file (optional)"}
            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              hidden
            />
          </FileUploadLabel>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? "Posting..." : "Post Project 🚀"}
          </SubmitButton>
        </Form>
      </Container>
    </Wrapper>
  );
};

export default PostFreelanceProject;

/* ---------------- Styled Components ---------------- */

const Wrapper = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  display: flex;
  justify-content: center;
  padding: 80px 20px;
`;

const Container = styled.div`
  max-width: 800px;
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.2);
  border-radius: 20px;
  padding: 40px;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 25px rgba(155, 92, 255, 0.2);

  h1 {
    background: linear-gradient(90deg, #9b5cff, #c59fff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-align: center;
  }

  p {
    text-align: center;
    color: #bbb;
    margin-bottom: 30px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  background: #1a1a1a;
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 10px;
  padding: 12px 16px;
  color: white;
  font-size: 1rem;
  outline: none;
  &:focus {
    border-color: #c59fff;
    box-shadow: 0 0 8px rgba(155, 92, 255, 0.4);
  }
`;

const TextArea = styled.textarea`
  background: #1a1a1a;
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 10px;
  padding: 12px 16px;
  color: white;
  font-size: 1rem;
  outline: none;
  min-height: 120px;
  resize: none;
  &:focus {
    border-color: #c59fff;
    box-shadow: 0 0 8px rgba(155, 92, 255, 0.4);
  }
`;

const FlexRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  flex: 1;
  background: #1a1a1a;
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 10px;
  padding: 12px;
  color: #fff;
  font-size: 1rem;
  outline: none;
`;

const SkillBox = styled.div`
  div {
    display: flex;
    gap: 10px;
  }
`;

const SkillInput = styled.input`
  flex: 1;
  background: #1a1a1a;
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 10px;
  padding: 10px;
  color: white;
`;

const AddSkillButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  cursor: pointer;
  font-weight: 600;
`;

const SkillTags = styled.div`
  margin-top: 10px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const SkillTag = styled.span`
  background: rgba(155, 92, 255, 0.2);
  color: #c59fff;
  border-radius: 8px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  svg {
    cursor: pointer;
  }
`;

const FileUploadLabel = styled.label`
  background: rgba(255, 255, 255, 0.05);
  border: 1px dashed rgba(155, 92, 255, 0.4);
  border-radius: 10px;
  padding: 14px;
  text-align: center;
  color: #ccc;
  cursor: pointer;
  transition: 0.3s;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;
  &:hover {
    transform: scale(1.03);
    box-shadow: 0 0 15px rgba(155, 92, 255, 0.4);
  }
`;
